import type {
  RailwayBucketProvisionResult,
  RailwayBucketSummary,
  RailwaySession
} from '@cli-types/next-adapter/railway.js'
import { outputTail } from '@cli-utils/process.js'
import { parseRailwayJson } from '@next-adapter/init/railway/runner/parse-railway-json.js'
import { runRailway } from '@next-adapter/init/railway/runner/run-railway.js'
import { parseBucketCredentials } from './parse-bucket-credentials.js'
import { parseBucket } from './parse-bucket.js'

export async function provisionRailwayBucketResource(
  session: RailwaySession,
  cwd: string,
  name: string,
  region: string,
  environmentArgs: string[]
): Promise<RailwayBucketProvisionResult> {
  const list = await runRailway(session.runner, ['bucket', 'list', ...environmentArgs, '--json'], {
    cwd,
    mode: 'capture',
    env: session.env
  })
  if (!list.success) {
    throw new Error(
      outputTail(`${list.stdout}\n${list.stderr}`) ?? 'Could not list Railway buckets.'
    )
  }

  const listPayload = parseRailwayJson<unknown>(list.stdout)
  if (!Array.isArray(listPayload)) {
    throw new Error('Railway returned an invalid bucket list response.')
  }

  const parsedBuckets = listPayload.map(parseBucket)
  if (parsedBuckets.some((bucket) => !bucket)) {
    throw new Error('Railway returned an invalid bucket list response.')
  }
  const buckets = parsedBuckets as RailwayBucketSummary[]
  let bucket = buckets.find((candidate) => candidate.name.toLowerCase() === name.toLowerCase())

  if (!bucket) {
    const create = await runRailway(
      session.runner,
      ['bucket', 'create', name, '--region', region, ...environmentArgs, '--json'],
      { cwd, mode: 'capture', env: session.env }
    )
    const createPayload = create.success
      ? parseRailwayJson<Record<string, unknown>>(create.stdout)
      : undefined
    bucket = createPayload ? parseBucket(createPayload) : undefined
    if (!create.success || !bucket) {
      throw new Error(
        outputTail(`${create.stdout}\n${create.stderr}`) ??
          `Could not create Railway bucket ${name}.`
      )
    }
    if (createPayload?.staged === true && createPayload.committed !== true) {
      throw new Error(
        `Railway staged bucket ${name} because the environment has pending changes. Commit those changes in Railway, then rerun init.`
      )
    }
  }

  const credentialsResult = await runRailway(
    session.runner,
    ['bucket', 'credentials', '--bucket', bucket.id, ...environmentArgs, '--json'],
    { cwd, mode: 'capture', env: session.env }
  )
  const credentials = credentialsResult.success
    ? parseBucketCredentials(parseRailwayJson<unknown>(credentialsResult.stdout))
    : undefined
  if (!credentials) {
    throw new Error(
      outputTail(`${credentialsResult.stdout}\n${credentialsResult.stderr}`) ??
        `Could not retrieve credentials for Railway bucket ${bucket.name}.`
    )
  }

  return { resource: bucket, credentials }
}
